/**
 * 关键词库主组件
 * 包含投放词库和否定词库的管理界面
 */
import React, { useState } from 'react';
import {
    Card,
    Tabs,
    Select,
    Space,
    Statistic,
    Row,
    Col,
    Badge,
    Typography,
    Spin,
    Empty,
} from 'antd';
import {
    TagsOutlined,
    StopOutlined,
    GlobalOutlined,
} from '@ant-design/icons';
import { TargetingKeywordTable } from './TargetingKeywordTable';
import { NegativeKeywordTable } from './NegativeKeywordTable';
import { useTargetingKeywords, useNegativeKeywords } from './hooks';
import { MARKETPLACE_OPTIONS, Marketplace } from './types';
import './styles.css';

const { Text } = Typography;

interface KeywordLibraryProps {
    skuId?: number;
}

export const KeywordLibrary: React.FC<KeywordLibraryProps> = ({ skuId }) => {
    const [marketplace, setMarketplace] = useState<Marketplace>('US');
    const [activeTab, setActiveTab] = useState('targeting');

    // 投放词库
    const {
        keywords: targetingKeywords,
        loading: targetingLoading,
        stats: targetingStats,
        addKeyword: addTargetingKeyword,
        updateKeyword: updateTargetingKeyword,
        deleteKeyword: deleteTargetingKeyword,
        batchDelete: batchDeleteTargeting,
        batchUpdateStatus: batchUpdateTargetingStatus,
        bulkImport: bulkImportTargeting,
    } = useTargetingKeywords(skuId, marketplace);

    // 否定词库
    const {
        keywords: negativeKeywords,
        loading: negativeLoading,
        stats: negativeStats,
        addKeyword: addNegativeKeyword,
        updateKeyword: updateNegativeKeyword,
        deleteKeyword: deleteNegativeKeyword,
        batchDelete: batchDeleteNegative,
        batchUpdateStatus: batchUpdateNegativeStatus,
        bulkImport: bulkImportNegative,
    } = useNegativeKeywords(skuId, marketplace);

    if (!skuId) {
        return (
            <Card bordered={false}>
                <Empty description="请先选择SKU" />
            </Card>
        );
    }

    const loading = targetingLoading || negativeLoading;

    // 统计卡片
    const renderStats = () => (
        <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
                <Card size="small" className="stat-card targeting">
                    <Statistic
                        title={<span><TagsOutlined /> 投放词</span>}
                        value={targetingStats?.total || 0}
                        suffix={
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                / {targetingStats?.active || 0} 启用
                            </Text>
                        }
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6}>
                <Card size="small" className="stat-card negative">
                    <Statistic
                        title={<span><StopOutlined /> 否定词</span>}
                        value={negativeStats?.total || 0}
                        suffix={
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                / {negativeStats?.active || 0} 启用
                            </Text>
                        }
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6}>
                <Card size="small" className="stat-card info">
                    <Statistic
                        title="ASIN否定"
                        value={negativeStats?.by_negative_type?.asin || 0}
                    />
                </Card>
            </Col>
            <Col xs={12} sm={6}>
                <Card size="small" className="stat-card info">
                    <Statistic
                        title="关键词否定"
                        value={
                            (negativeStats?.by_negative_type?.keyword_exact || 0) +
                            (negativeStats?.by_negative_type?.keyword_phrase || 0)
                        }
                    />
                </Card>
            </Col>
        </Row>
    );

    // 确保数据是数组
    const safeTargetingKeywords = Array.isArray(targetingKeywords) ? targetingKeywords : [];
    const safeNegativeKeywords = Array.isArray(negativeKeywords) ? negativeKeywords : [];

    const tabItems = [
        {
            key: 'targeting',
            label: (
                <span>
                    <TagsOutlined />
                    投放词库
                    <Badge
                        count={targetingStats?.total || 0}
                        style={{ marginLeft: 8, backgroundColor: '#52c41a' }}
                        overflowCount={999}
                    />
                </span>
            ),
            children: (
                <TargetingKeywordTable
                    keywords={safeTargetingKeywords}
                    loading={targetingLoading}
                    onAdd={addTargetingKeyword}
                    onBulkImport={bulkImportTargeting}
                    onUpdate={updateTargetingKeyword}
                    onDelete={deleteTargetingKeyword}
                    onBatchDelete={batchDeleteTargeting}
                    onBatchUpdateStatus={batchUpdateTargetingStatus}
                />
            ),
        },
        {
            key: 'negative',
            label: (
                <span>
                    <StopOutlined />
                    否定词库
                    <Badge
                        count={negativeStats?.total || 0}
                        style={{ marginLeft: 8, backgroundColor: '#ff4d4f' }}
                        overflowCount={999}
                    />
                </span>
            ),
            children: (
                <NegativeKeywordTable
                    keywords={safeNegativeKeywords}
                    loading={negativeLoading}
                    onAdd={addNegativeKeyword}
                    onBulkImport={bulkImportNegative}
                    onUpdate={updateNegativeKeyword}
                    onDelete={deleteNegativeKeyword}
                    onBatchDelete={batchDeleteNegative}
                    onBatchUpdateStatus={batchUpdateNegativeStatus}
                />
            ),
        },
    ];

    return (
        <div className="keyword-library">
            <Spin spinning={loading}>
                {/* 站点选择器 */}
                <div className="marketplace-selector">
                    <Space>
                        <GlobalOutlined />
                        <Text strong>站点:</Text>
                        <Select
                            value={marketplace}
                            onChange={setMarketplace}
                            style={{ width: 140 }}
                            options={Object.entries(MARKETPLACE_OPTIONS).map(([value, label]) => ({
                                value,
                                label: `${value} - ${label}`,
                            }))}
                        />
                    </Space>
                </div>

                {/* 统计卡片 */}
                {renderStats()}

                {/* 词库标签页 */}
                <Card bordered={false} className="keyword-tabs-card">
                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems}
                        destroyInactiveTabPane
                    />
                </Card>
            </Spin>
        </div>
    );
};

export default KeywordLibrary;
